import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Descriptions, Input, InputNumber, Modal, Space, Typography, message } from 'antd';
import { ConsignmentItem } from '../../model/consignment';

interface AddStockPayload {
    consignment_item_id: number;
    add_quantity: number;
    selling_price_per_unit: number;
    owner_cost_per_unit: number;
    note?: string;
}

interface Props {
    open: boolean;
    item: ConsignmentItem | null;
    onClose: () => void;
    onAddStock: (payload: AddStockPayload) => Promise<any>;
}

export const ConsignmentAddStockModal: React.FC<Props> = ({ open, item, onClose, onAddStock }) => {
    // Consignment quantities are whole pieces only (locked product rule).
    // The field starts EMPTY (undefined), not a real 0/1 -- "0" shown below
    // is a visual placeholder only, never submitted as a real value.
    const [addQuantity, setAddQuantity] = useState<number | undefined>(undefined);
    const [sellingPrice, setSellingPrice] = useState<number>(0);
    const [ownerCost, setOwnerCost] = useState<number>(0);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !item) return;
        setAddQuantity(undefined);
        setSellingPrice(Number(item.selling_price_per_unit || 0));
        setOwnerCost(Number(item.owner_cost_per_unit || 0));
        setNote('');
    }, [open, item]);

    const currentRemaining = Number(item?.latest_actual_remaining || 0);
    const afterQuantity = currentRemaining + Math.max(0, Number(addQuantity ?? 0));
    const termsChanged = useMemo(() => {
        if (!item) return false;
        return Number(sellingPrice || 0).toFixed(2) !== Number(item.selling_price_per_unit || 0).toFixed(2)
            || Number(ownerCost || 0).toFixed(2) !== Number(item.owner_cost_per_unit || 0).toFixed(2);
    }, [item, ownerCost, sellingPrice]);

    const handleConfirm = async () => {
        if (!item?.id) {
            message.error('ไม่พบรายการสินค้าที่ต้องการฝากเพิ่ม');
            return;
        }
        if (addQuantity === undefined || addQuantity === null) {
            message.error('กรุณาระบุจำนวนที่ฝากเพิ่ม');
            return;
        }
        if (!Number.isInteger(addQuantity)) {
            message.error('จำนวนที่ฝากเพิ่มต้องเป็นจำนวนเต็ม (ไม่รองรับทศนิยม) เนื่องจากสินค้าฝากขายนับเป็นชิ้น');
            return;
        }
        if (addQuantity <= 0) {
            message.error('จำนวนที่ฝากเพิ่มต้องมากกว่า 0');
            return;
        }
        if (!Number.isFinite(sellingPrice) || Number(sellingPrice) < 0) {
            message.error('ราคาขายใหม่ไม่ถูกต้อง');
            return;
        }
        if (!Number.isFinite(ownerCost) || Number(ownerCost) < 0) {
            message.error('ต้นทุนผู้ฝากใหม่ไม่ถูกต้อง');
            return;
        }

        setSaving(true);
        try {
            await onAddStock({
                consignment_item_id: item.id,
                add_quantity: Number(addQuantity),
                selling_price_per_unit: Number(sellingPrice),
                owner_cost_per_unit: Number(ownerCost),
                note: note.trim() || undefined
            });
            onClose();
        } catch (error: any) {
            console.error('Failed to add stock', error);
            message.error(error?.message || 'บันทึกการฝากเพิ่มไม่สำเร็จ');
        } finally {
            setSaving(false);
        }
    };

    if (!item) return null;

    return (
        <Modal
            open={open}
            rootClassName="consignment-add-stock-modal"
            title={(
                <div className="consignment-add-stock-modal-title">
                    <span className="consignment-add-stock-modal-title-main">📥 ฝากเพิ่มสินค้า</span>
                    <span className="consignment-add-stock-modal-title-sub">
                        {item.product_name || `สินค้า #${item.product_id}`} · Batch #{item.batch_no || 1}
                    </span>
                </div>
            )}
            okText="ยืนยันฝากเพิ่ม"
            cancelText="ยกเลิก"
            confirmLoading={saving}
            onOk={handleConfirm}
            onCancel={onClose}
            destroyOnClose
            width={620}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div className="consignment-add-stock-context-banner">
                    <span className="consignment-add-stock-context-icon">+</span>
                    <div>
                        <strong>เพิ่มสินค้าฝากขายในรายการนี้</strong>
                        <span>ระบุจำนวนและเงื่อนไขของสินค้าที่นำมาฝากเพิ่ม</span>
                    </div>
                </div>
                <Descriptions className="consignment-add-stock-summary" bordered size="small" column={1}>
                    <Descriptions.Item label="Batch ปัจจุบัน">#{item.batch_no || 1}</Descriptions.Item>
                    <Descriptions.Item label="คงเหลือปัจจุบัน">
                        <strong data-testid="add-stock-current">{currentRemaining} {item.product_unit_name || 'หน่วย'}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="จำนวนฝากเพิ่ม">
                        <InputNumber
                            min={1}
                            step={1}
                            precision={0}
                            placeholder="0"
                            value={addQuantity}
                            onChange={value => setAddQuantity(value === null ? undefined : Number(value))}
                            aria-label="จำนวนฝากเพิ่ม"
                            data-testid="input-add-stock-quantity"
                            style={{ width: 180 }}
                            addonAfter={item.product_unit_name || 'หน่วย'}
                        />
                    </Descriptions.Item>
                    <Descriptions.Item label="หลังฝากเพิ่ม">
                        <Typography.Text className="consignment-add-stock-after" strong type="success" data-testid="add-stock-after">
                            {afterQuantity} {item.product_unit_name || 'หน่วย'}
                        </Typography.Text>
                    </Descriptions.Item>
                </Descriptions>

                <div className="consignment-add-stock-terms">
                    <Typography.Text className="consignment-add-stock-section-title">
                        เงื่อนไขทางการเงินของของฝากรอบนี้
                    </Typography.Text>
                    <div className="consignment-add-stock-price-grid">
                        <label>
                            <span className="consignment-add-stock-field-label">ราคาขายต่อหน่วย</span>
                            <InputNumber
                                min={0}
                                precision={2}
                                value={sellingPrice}
                                onChange={value => setSellingPrice(Number(value || 0))}
                                aria-label="ราคาขายใหม่"
                                data-testid="input-add-stock-selling-price"
                                addonBefore="฿"
                                style={{ width: '100%' }}
                            />
                        </label>
                        <label>
                            <span className="consignment-add-stock-field-label">ต้นทุนผู้ฝากต่อหน่วย</span>
                            <InputNumber
                                min={0}
                                precision={2}
                                value={ownerCost}
                                onChange={value => setOwnerCost(Number(value || 0))}
                                aria-label="ต้นทุนผู้ฝากใหม่"
                                data-testid="input-add-stock-owner-cost"
                                addonBefore="฿"
                                style={{ width: '100%' }}
                            />
                        </label>
                    </div>
                </div>

                {termsChanged && (
                    <Alert
                        type="warning"
                        showIcon
                        data-testid="add-stock-terms-warning"
                        message="เงื่อนไขราคาของสินค้ารอบใหม่นี้ไม่ตรงกับรายการเดิม"
                        description={(
                            <div>
                                <div>ราคาขายเดิม: ฿{Number(item.selling_price_per_unit).toFixed(2)} / ราคาขายใหม่: ฿{Number(sellingPrice).toFixed(2)}</div>
                                <div>ต้นทุนผู้ฝากเดิม: ฿{Number(item.owner_cost_per_unit).toFixed(2)} / ต้นทุนผู้ฝากใหม่: ฿{Number(ownerCost).toFixed(2)}</div>
                                <strong>ระบบจะสร้าง Batch ใหม่เพื่อแยกต้นทุน โดยไม่เฉลี่ยหรือแก้ข้อมูลเดิม</strong>
                            </div>
                        )}
                    />
                )}

                <div className="consignment-add-stock-note">
                    <Typography.Text className="consignment-add-stock-field-label">หมายเหตุ</Typography.Text>
                    <Input
                        value={note}
                        onChange={event => setNote(event.target.value)}
                        placeholder="เช่น ฝากเพิ่มรอบบ่าย"
                        data-testid="input-add-stock-note"
                    />
                </div>
            </Space>
        </Modal>
    );
};

export default ConsignmentAddStockModal;
